/**
 * Every petition whose carousel impression has already been recorded this pageload.
 *
 * Module-scoped, so the two mounted `MobileActionButtonContainer` columns — both rendered, one hidden by
 * CSS — share one guard across a breakpoint-crossing resize.
 *
 * A Set, not the last petition id alone: with a single slot, whether a repeat view of petition A counts
 * depends on whether an unrelated petition was visited in between (B overwrites the slot, so returning to
 * A re-fires; a B whose carousel never rendered leaves the slot alone, so it doesn't). That made the event
 * neither once-per-petition nor once-per-view, but a function of browsing path — useless as a denominator.
 *
 * The cost of the Set is deliberate and worth stating: an A → B → A journey reports A once, while
 * `$exposure` re-fires for A on the return leg (`useCallOnce` remembers only its last deps). So impressions
 * are "petitions whose carousel this session saw", which is ≤ exposures rather than equal to them.
 */
const reportedPetitionIds = new Set<string>();

/**
 * Claims the single carousel impression for `petitionId`, returning whether this call is the one that won
 * it. Every later call for the same petition returns `false` for the rest of the pageload.
 *
 * Lives in its own module rather than beside the component so the state can be exercised directly, without
 * the component exporting a reset purely for tests.
 */
export function claimCarouselImpression(petitionId: string): boolean {
	if (reportedPetitionIds.has(petitionId)) {
		return false;
	}
	reportedPetitionIds.add(petitionId);

	return true;
}
